/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { CommerceList, ProductPrice, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';

import * as React from 'react';

import { ProductComponent } from '../../../shared/components/product.component';
import { IPriceComponentResources } from '../../../shared/components/price.component';

import { IExtSearchResultContainerResources } from '../ext-search-result-container.props.autogenerated';
import { ProductSearchResultExpanded } from '../../../shared/classes/ProductSearchResultExpanded';
import { usePromoBlocks } from '../hooks';
import { useMemo } from 'react';

interface IProductSearchResultItems {
    products: ProductSearchResultExpanded[];
    context: ICoreContext;
    resources: IExtSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    wishlist?: CommerceList[];
    wishlistInProductCard?: boolean;
    productPrices?: ProductPrice[];
    repeat?: boolean;
    startPosition?: number;
    frequency?: number;
    promoBlocks: React.ReactNode[];
    enablePromoBlocks: boolean;
    hideProductsWithoutImages?: boolean;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
export function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    wishlist,
    wishlistInProductCard,
    products,
    repeat = false,
    startPosition,
    frequency,
    promoBlocks,
    enablePromoBlocks,
    hideProductsWithoutImages
}) => {
    const filteredProducts = useMemo(() => {
        if (!hideProductsWithoutImages) {
            return products;
        }
        return products.filter(product =>
            product.AttributeValues?.find(attribute => attribute.Name === 'Color')
                ?.Swatches?.find(swatch => swatch.IsDefault)
                ?.ProductImageUrls?.some(image => image !== '')
        );
    }, [products, hideProductsWithoutImages]);
    const { renderItems } = usePromoBlocks({
        enablePromoBlocks,
        products: filteredProducts,
        promoBlocks,
        repeat,
        frequency,
        startPosition
    });
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator,
        memberPriceMessage: resources.memberPriceMessage,
        saleMessage: resources.saleMessage
    };
    return (
        <ul className='list-unstyled' data-testid='plp-product-list'>
            {renderItems.map(item => {
                if (item.kind === 'promo') {
                    return (
                        <li className='ms-product-search-result__item' key={item.key}>
                            {item.promo}
                        </li>
                    );
                }

                const product = item.product;

                return (
                    <li className='ms-product-search-result__item' key={item.key} data-testid='plp-product-card'>
                        <ProductComponent
                            context={context}
                            telemetryContent={telemetryContent}
                            imageSettings={imageSettings}
                            freePriceText={resources.priceFree}
                            originalPriceText={resources.originalPriceText}
                            currentPriceText={resources.currentPriceText}
                            ratingAriaLabel={resources.ratingAriaLabel}
                            allowBack={allowBackNavigation}
                            id={moduleId}
                            typeName={moduleType}
                            data={{ product }}
                            quickViewButton={quickviewSlot}
                            productComparisonButton={productComparisonButton}
                            inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                            swatchItemAriaLabel={resources.swatchItemAriaLabel}
                            dimensionAvailabilities={productsDimensionAvailabilities?.find(
                                dimensionAvailability =>
                                    ArrayExtensions.hasElements(dimensionAvailability) &&
                                    dimensionAvailability[0].masterProductId === product.MasterProductId
                            )}
                            wishlist={wishlist}
                            wishlistInProductCard={wishlistInProductCard}
                            wishlistResources={{
                                addToWishlistButtonText: resources.addToWishlistButtonText,
                                removeFromWishlistButtonText: resources.removeFromWishlistButtonText
                            }}
                        />
                    </li>
                );
            })}
        </ul>
    );
};
